import React, { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import useGameSocket from "@/lib/useGameSocket";
import GameInviteModal from "@/components/GameInviteModal";
import { Users, Wifi, Copy, ArrowLeft, Eye } from "lucide-react";

/**
 * Paddle Clash — 2-player paddle duel with two modes:
 *
 *   • LOCAL  — hot-seat, both players on the same keyboard (W/S vs ↑/↓)
 *   • ONLINE — host runs authoritative physics + broadcasts state at 30Hz
 *              through /api/game-ws/{roomId}. Guest sends its paddle input
 *              and renders received state.
 *
 * Props:
 *   joinRoomId — if set on mount, jump straight into ONLINE as guest for
 *                this room (used when accepting an invite).
 */
const W = 800;
const H = 480;
const PADDLE_W = 12;
const PADDLE_H = 84;
const PADDLE_SPEED = 6.2;
const BALL_R = 8;
const BALL_START_SPEED = 5.4;
const BALL_MAX_SPEED = 11;
const WIN_SCORE = 7;
const NET_TICK_MS = 33;   // ~30Hz state broadcast

const sign = () => (Math.random() < 0.5 ? -1 : 1);

function initialBall(dir = 1) {
  const angle = (Math.random() * 0.6 - 0.3);
  return {
    x: W / 2, y: H / 2,
    vx: dir * BALL_START_SPEED * Math.cos(angle),
    vy: BALL_START_SPEED * Math.sin(angle) * (Math.random() < 0.5 ? -1 : 1),
  };
}

export default function PaddleClash({ joinRoomId = null, spectate = false }) {
  // stage: "start" | "waiting" | "playing"
  // mode:  "local" | "host" | "guest" | "spectator"
  const [stage, setStage] = useState(joinRoomId ? "waiting" : "start");
  const [mode, setMode] = useState(joinRoomId ? (spectate ? "spectator" : "guest") : null);
  const [roomId, setRoomId] = useState(joinRoomId);
  const [peerName, setPeerName] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [error, setError] = useState("");
  const [specCount, setSpecCount] = useState(0);

  // Refs for the tight animation loop
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  const keysRef = useRef({ w: false, s: false, up: false, down: false });
  const paddlesRef = useRef({ ly: H / 2 - PADDLE_H / 2, ry: H / 2 - PADDLE_H / 2 });
  const ballRef = useRef(initialBall(sign()));
  const scoreRef = useRef({ l: 0, r: 0 });
  const runningRef = useRef(true);
  const lastNetSendRef = useRef(0);
  const guestInputRef = useRef({ py: 0.5 }); // guest paddle target (normalized)

  const [score, setScore] = useState({ l: 0, r: 0 });
  const [winner, setWinner] = useState(null);
  const [paused, setPaused] = useState(false);

  // --- Guest-only: buffered latest state from host to render ---
  const guestStateRef = useRef(null);

  // ---- Networking ----
  const onNetMessage = useCallback((m) => {
    if (m.type === "peer_joined") {
      // Host learns guest is here → start the match
      if (typeof m.spectator_count === "number") setSpecCount(m.spectator_count);
      if (m.side !== "spectator" && m.username) setPeerName(prev => prev || m.username);
      if (mode === "host" && m.side === "guest") {
        setStage("playing");
      }
    } else if (m.type === "peer_left") {
      if (typeof m.spectator_count === "number") setSpecCount(m.spectator_count);
      if (m.side !== "spectator") {
        setError(`${m.username || "Peer"} disconnected`);
        runningRef.current = false;
      }
    } else if (m.type === "input" && mode === "host") {
      // Guest sent its paddle position
      if (typeof m.py === "number") {
        guestInputRef.current.py = Math.max(0, Math.min(1, m.py));
      }
    } else if (m.type === "state" && (mode === "guest" || mode === "spectator")) {
      guestStateRef.current = m;
      // Also mirror score in React state so HUD updates
      if (m.ls != null && m.rs != null) setScore({ l: m.ls, r: m.rs });
      if (m.winner) { setWinner(m.winner); runningRef.current = false; }
    } else if (m.type === "restart") {
      // Peer requested a fresh match
      paddlesRef.current = { ly: H / 2 - PADDLE_H / 2, ry: H / 2 - PADDLE_H / 2 };
      ballRef.current = initialBall(sign());
      scoreRef.current = { l: 0, r: 0 };
      setScore({ l: 0, r: 0 });
      setWinner(null);
      setPaused(false);
      runningRef.current = true;
    }
  }, [mode]);

  const socket = useGameSocket({
    roomId,
    enabled: mode === "host" || mode === "guest" || mode === "spectator",
    onMessage: onNetMessage,
  });

  // Guest/spectator: as soon as socket connects, we're already joined.
  useEffect(() => {
    if ((mode === "guest" || mode === "spectator") && socket.connected && stage === "waiting") {
      setStage("playing");
    }
  }, [mode, socket.connected, stage]);

  useEffect(() => {
    if (socket.hello) {
      if (typeof socket.hello.spectator_count === "number") setSpecCount(socket.hello.spectator_count);
      const other = socket.hello.side === "host" ? socket.hello.guest : socket.hello.host;
      if (other) setPeerName(other);
      // Backend may downgrade us to spectator if the room is already full.
      if (socket.hello.side === "spectator") setMode("spectator");
    }
  }, [socket.hello]);

  // ---- Mode selection helpers ----
  const startLocal = () => {
    setMode("local");
    setStage("playing");
    resetGame();
  };

  const startOnline = async () => {
    setError("");
    try {
      const r = await api.post("/games/rooms", { slug: "pong" });
      const rid = r.data?.room?.id;
      if (!rid) throw new Error("no room id");
      setRoomId(rid);
      setMode("host");
      setStage("waiting");
      setInviteOpen(true);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't create room");
    }
  };

  const cancelOnline = () => {
    socket.close();
    setMode(null);
    setRoomId(null);
    setStage("start");
    setInviteOpen(false);
  };

  const resetGame = () => {
    paddlesRef.current = { ly: H / 2 - PADDLE_H / 2, ry: H / 2 - PADDLE_H / 2 };
    ballRef.current = initialBall(sign());
    scoreRef.current = { l: 0, r: 0 };
    setScore({ l: 0, r: 0 });
    setWinner(null);
    setPaused(false);
    runningRef.current = true;
  };

  const restartMatch = () => {
    resetGame();
    if (mode === "host" || mode === "guest") socket.send({ type: "restart" });
  };

  // ---- Keyboard input ----
  useEffect(() => {
    if (stage !== "playing") return;
    if (mode === "spectator") return;
    const onDown = (e) => {
      const k = e.key.toLowerCase();
      if (mode === "local") {
        if (k === "w") keysRef.current.w = true;
        else if (k === "s") keysRef.current.s = true;
        else if (e.key === "ArrowUp") { keysRef.current.up = true; e.preventDefault(); }
        else if (e.key === "ArrowDown") { keysRef.current.down = true; e.preventDefault(); }
      } else if (mode === "host") {
        // Host controls left paddle only (W/S or arrows both work)
        if (k === "w" || e.key === "ArrowUp") { keysRef.current.w = true; e.preventDefault(); }
        else if (k === "s" || e.key === "ArrowDown") { keysRef.current.s = true; e.preventDefault(); }
      } else if (mode === "guest") {
        // Guest controls right paddle
        if (k === "w" || e.key === "ArrowUp") { keysRef.current.up = true; e.preventDefault(); }
        else if (k === "s" || e.key === "ArrowDown") { keysRef.current.down = true; e.preventDefault(); }
      }
      if (k === " ") { setPaused(p => !p); e.preventDefault(); }
    };
    const onUp = (e) => {
      const k = e.key.toLowerCase();
      if (mode === "local") {
        if (k === "w") keysRef.current.w = false;
        else if (k === "s") keysRef.current.s = false;
        else if (e.key === "ArrowUp") keysRef.current.up = false;
        else if (e.key === "ArrowDown") keysRef.current.down = false;
      } else if (mode === "host") {
        if (k === "w" || e.key === "ArrowUp") keysRef.current.w = false;
        else if (k === "s" || e.key === "ArrowDown") keysRef.current.s = false;
      } else if (mode === "guest") {
        if (k === "w" || e.key === "ArrowUp") keysRef.current.up = false;
        else if (k === "s" || e.key === "ArrowDown") keysRef.current.down = false;
      }
    };
    window.addEventListener("keydown", onDown);
    window.addEventListener("keyup", onUp);
    return () => {
      window.removeEventListener("keydown", onDown);
      window.removeEventListener("keyup", onUp);
    };
  }, [stage, mode]);

  // ---- Main render / physics loop ----
  useEffect(() => {
    if (stage !== "playing") return;
    const cv = canvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext("2d");
    let last = performance.now();

    const step = (now) => {
      const dt = Math.min(2, (now - last) / 16.666);
      last = now;

      if (runningRef.current && !paused && !winner) {
        if (mode === "local" || mode === "host") {
          // Simulate authoritative physics
          const k = keysRef.current;
          const p = paddlesRef.current;
          // Local: keys W/S = left, up/down = right
          // Host:  keys W/S(or ↑/↓) = left,  right paddle driven by guest input
          if (mode === "local") {
            if (k.w) p.ly -= PADDLE_SPEED * dt;
            if (k.s) p.ly += PADDLE_SPEED * dt;
            if (k.up)   p.ry -= PADDLE_SPEED * dt;
            if (k.down) p.ry += PADDLE_SPEED * dt;
          } else {
            // Host physics
            if (k.w) p.ly -= PADDLE_SPEED * dt;
            if (k.s) p.ly += PADDLE_SPEED * dt;
            // Guest target position → smooth move toward it
            const targetY = guestInputRef.current.py * (H - PADDLE_H);
            const diff = targetY - p.ry;
            const step_ = Math.sign(diff) * Math.min(Math.abs(diff), PADDLE_SPEED * 1.15 * dt);
            p.ry += step_;
          }
          p.ly = Math.max(0, Math.min(H - PADDLE_H, p.ly));
          p.ry = Math.max(0, Math.min(H - PADDLE_H, p.ry));

          // Ball physics
          const b = ballRef.current;
          b.x += b.vx * dt;
          b.y += b.vy * dt;
          if (b.y - BALL_R < 0) { b.y = BALL_R; b.vy = Math.abs(b.vy); }
          if (b.y + BALL_R > H) { b.y = H - BALL_R; b.vy = -Math.abs(b.vy); }
          if (b.vx < 0 && b.x - BALL_R < 20 + PADDLE_W && b.x - BALL_R > 12 &&
              b.y > p.ly - BALL_R && b.y < p.ly + PADDLE_H + BALL_R) {
            const rel = (b.y - (p.ly + PADDLE_H / 2)) / (PADDLE_H / 2);
            const speed = Math.min(BALL_MAX_SPEED, Math.hypot(b.vx, b.vy) * 1.05);
            const angle = rel * 0.9;
            b.vx = Math.abs(speed * Math.cos(angle));
            b.vy = speed * Math.sin(angle);
          }
          const rightX = W - 20 - PADDLE_W;
          if (b.vx > 0 && b.x + BALL_R > rightX && b.x + BALL_R < rightX + PADDLE_W + 8 &&
              b.y > p.ry - BALL_R && b.y < p.ry + PADDLE_H + BALL_R) {
            const rel = (b.y - (p.ry + PADDLE_H / 2)) / (PADDLE_H / 2);
            const speed = Math.min(BALL_MAX_SPEED, Math.hypot(b.vx, b.vy) * 1.05);
            const angle = rel * 0.9;
            b.vx = -Math.abs(speed * Math.cos(angle));
            b.vy = speed * Math.sin(angle);
          }
          if (b.x < -BALL_R) {
            scoreRef.current.r += 1;
            setScore({ ...scoreRef.current });
            if (scoreRef.current.r >= WIN_SCORE) { setWinner("R"); runningRef.current = false; }
            else ballRef.current = initialBall(-1);
          } else if (b.x > W + BALL_R) {
            scoreRef.current.l += 1;
            setScore({ ...scoreRef.current });
            if (scoreRef.current.l >= WIN_SCORE) { setWinner("L"); runningRef.current = false; }
            else ballRef.current = initialBall(1);
          }

          // Host: broadcast state at ~30Hz
          if (mode === "host" && now - lastNetSendRef.current > NET_TICK_MS) {
            lastNetSendRef.current = now;
            const w2 = winner ? winner : null;
            socket.send({
              type: "state",
              ly: p.ly, ry: p.ry,
              bx: b.x, by: b.y, vx: b.vx, vy: b.vy,
              ls: scoreRef.current.l, rs: scoreRef.current.r,
              winner: w2,
            });
          }
        } else if (mode === "guest") {
          // Guest: apply local input → send to host every frame throttled to 30Hz
          const k = keysRef.current;
          const p = paddlesRef.current;
          if (k.up)   p.ry -= PADDLE_SPEED * dt;
          if (k.down) p.ry += PADDLE_SPEED * dt;
          p.ry = Math.max(0, Math.min(H - PADDLE_H, p.ry));
          if (now - lastNetSendRef.current > NET_TICK_MS) {
            lastNetSendRef.current = now;
            socket.send({ type: "input", py: p.ry / (H - PADDLE_H) });
          }
          // Overlay host-authoritative state
          const s = guestStateRef.current;
          if (s) {
            p.ly = s.ly;
            ballRef.current = { x: s.bx, y: s.by, vx: s.vx, vy: s.vy };
          }
        } else if (mode === "spectator") {
          // Spectator: purely read-only render of host snapshots
          const s = guestStateRef.current;
          if (s) {
            const p = paddlesRef.current;
            p.ly = s.ly;
            p.ry = s.ry;
            ballRef.current = { x: s.bx, y: s.by, vx: s.vx, vy: s.vy };
          }
        }
      }

      // ------- Render -------
      ctx.fillStyle = "#050508";
      ctx.fillRect(0, 0, W, H);
      ctx.strokeStyle = "rgba(255,255,255,0.15)";
      ctx.setLineDash([8, 12]);
      ctx.beginPath(); ctx.moveTo(W / 2, 0); ctx.lineTo(W / 2, H); ctx.stroke();
      ctx.setLineDash([]);
      ctx.font = "bold 96px system-ui, sans-serif";
      ctx.fillStyle = "rgba(255,255,255,0.06)";
      ctx.textAlign = "center";
      ctx.fillText(scoreRef.current.l, W * 0.28, H * 0.6);
      ctx.fillText(scoreRef.current.r, W * 0.72, H * 0.6);
      const p = paddlesRef.current;
      ctx.fillStyle = "#00E5FF";
      ctx.shadowColor = "#00E5FF"; ctx.shadowBlur = 18;
      ctx.fillRect(20, p.ly, PADDLE_W, PADDLE_H);
      ctx.fillStyle = "#FF1053";
      ctx.shadowColor = "#FF1053";
      ctx.fillRect(W - 20 - PADDLE_W, p.ry, PADDLE_W, PADDLE_H);
      const b = ballRef.current;
      ctx.fillStyle = "#CCFF00";
      ctx.shadowColor = "#CCFF00"; ctx.shadowBlur = 20;
      ctx.beginPath(); ctx.arc(b.x, b.y, BALL_R, 0, Math.PI * 2); ctx.fill();
      ctx.shadowBlur = 0;

      rafRef.current = requestAnimationFrame(step);
    };

    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [stage, mode, paused, winner, socket]);

  // Guest: join the room via REST before opening socket
  useEffect(() => {
    if (mode === "guest" && roomId) {
      api.post(`/games/rooms/${roomId}/join`).catch((e) => {
        setError(e?.response?.data?.detail || "Couldn't join room");
      });
    }
  }, [mode, roomId]);

  // ---------------- UI ----------------
  if (stage === "start") {
    return (
      <div className="max-w-md mx-auto" data-testid="pc-start">
        <h2 className="sc-heading text-2xl font-black mb-4">Choose your mode</h2>
        <div className="grid gap-3">
          <button
            data-testid="pc-mode-local"
            onClick={startLocal}
            className="sc-card p-5 text-left hover:-translate-y-0.5 transition"
          >
            <div className="sc-label" style={{ color: "var(--sc-volt)" }}>Hot Seat</div>
            <div className="sc-heading text-xl font-bold mt-1">Play locally</div>
            <div className="text-xs text-zinc-500 mt-1 sc-mono">Both players on this keyboard · W/S vs ↑/↓</div>
          </button>
          <button
            data-testid="pc-mode-online"
            onClick={startOnline}
            className="sc-card p-5 text-left hover:-translate-y-0.5 transition"
          >
            <div className="sc-label" style={{ color: "var(--sc-pink)" }}>Online</div>
            <div className="sc-heading text-xl font-bold mt-1 flex items-center gap-2"><Users className="w-5 h-5" /> Invite a friend</div>
            <div className="text-xs text-zinc-500 mt-1 sc-mono">You get cyan · friend gets pink · real-time WS</div>
          </button>
        </div>
        {error && <div className="text-xs text-[color:var(--sc-pink)] mt-3 sc-mono" data-testid="pc-error">{error}</div>}
      </div>
    );
  }

  if (stage === "waiting") {
    const inviteLink   = roomId ? `${window.location.origin}/games?join_room=${roomId}&slug=pong` : "";
    const spectateLink = roomId ? `${window.location.origin}/games?join_room=${roomId}&slug=pong&spectate=1` : "";
    return (
      <div className="max-w-md mx-auto text-center" data-testid="pc-waiting">
        <div className="sc-label" style={{ color: "var(--sc-pink)" }}>{mode === "host" ? "Waiting for opponent" : "Joining room…"}</div>
        <h2 className="sc-heading text-2xl font-black mt-1 mb-3">
          {mode === "host" ? "Send them an invite" : "Connecting"}
        </h2>
        <div className="sc-card p-5 space-y-3">
          {mode === "host" && (
            <>
              <button
                data-testid="pc-open-invite"
                onClick={() => setInviteOpen(true)}
                className="sc-btn w-full text-sm"
              >
                Invite a friend
              </button>
              <div className="text-[10px] sc-mono tracking-widest uppercase text-zinc-500">— or share these links —</div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-zinc-500 w-14 text-left sc-mono">Play</span>
                  <input readOnly value={inviteLink} className="sc-input flex-1 text-xs" data-testid="pc-invite-link" />
                  <button
                    onClick={() => { try { navigator.clipboard.writeText(inviteLink); } catch { /* noop */ } }}
                    data-testid="pc-copy-link"
                    className="sc-btn-ghost text-xs flex items-center gap-1"
                  ><Copy className="w-3 h-3" /></button>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-zinc-500 w-14 text-left sc-mono flex items-center gap-1"><Eye className="w-3 h-3" />Watch</span>
                  <input readOnly value={spectateLink} className="sc-input flex-1 text-xs" data-testid="pc-spectate-link" />
                  <button
                    onClick={() => { try { navigator.clipboard.writeText(spectateLink); } catch { /* noop */ } }}
                    data-testid="pc-copy-spectate"
                    className="sc-btn-ghost text-xs flex items-center gap-1"
                  ><Copy className="w-3 h-3" /></button>
                </div>
              </div>
              <div className="text-xs text-zinc-500 sc-mono flex items-center gap-1.5 justify-center pt-1">
                <Wifi className={`w-3.5 h-3.5 ${socket.connected ? "text-[color:var(--sc-volt)]" : "text-zinc-600"}`} />
                {socket.connected ? "Room ready" : "Opening room…"}
              </div>
            </>
          )}
          {mode !== "host" && (
            <div className="py-4">
              <div className="text-sm text-zinc-300">
                {socket.connected ? "Joined! Starting…" : "Connecting…"}
              </div>
              {error && <div className="text-xs text-[color:var(--sc-pink)] mt-2 sc-mono">{error}</div>}
            </div>
          )}
        </div>
        <button onClick={cancelOnline} className="sc-btn-ghost text-xs mt-4 inline-flex items-center gap-2">
          <ArrowLeft className="w-3.5 h-3.5" /> Cancel
        </button>
        <GameInviteModal
          open={inviteOpen}
          onClose={() => setInviteOpen(false)}
          roomId={roomId}
          gameName="Paddle Clash"
        />
      </div>
    );
  }

  // stage === "playing"
  const myLabel = mode === "local"
    ? "P1 · W / S"
    : mode === "host" ? "You · W / S (or ↑ / ↓)" : "You · W / S (or ↑ / ↓)";
  const opLabel = mode === "local"
    ? "P2 · ↑ / ↓"
    : `@${peerName || "opponent"}`;

  return (
    <div data-testid="game-paddle-clash" className="flex flex-col items-center gap-3">
      <div className="w-full max-w-[800px] flex items-center justify-between px-1">
        <div className="flex items-center gap-2">
          <span className="sc-mono text-xs tracking-widest uppercase text-[color:var(--sc-cyan)]">{myLabel}</span>
          <span className="text-2xl font-black tabular-nums text-[color:var(--sc-cyan)]" data-testid="pc-score-left">{score.l}</span>
        </div>
        <div className="flex flex-col items-center">
          <div className="sc-mono text-[10px] tracking-widest uppercase text-zinc-500">FIRST TO {WIN_SCORE}</div>
          {mode !== "local" && (
            <div className="sc-mono text-[9px] tracking-widest uppercase mt-0.5 flex items-center gap-2">
              <span className="flex items-center gap-1">
                <Wifi className={`w-3 h-3 ${socket.connected ? "text-[color:var(--sc-volt)]" : "text-[color:var(--sc-pink)]"}`} />
                <span className={socket.connected ? "text-[color:var(--sc-volt)]" : "text-[color:var(--sc-pink)]"}>
                  {socket.connected ? "LIVE" : "OFFLINE"}
                </span>
              </span>
              {specCount > 0 && (
                <span className="flex items-center gap-1 text-zinc-400"><Eye className="w-3 h-3" />{specCount}</span>
              )}
              {mode === "spectator" && (
                <span className="text-[color:var(--sc-cyan)]">SPECTATING</span>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-2xl font-black tabular-nums text-[color:var(--sc-pink)]" data-testid="pc-score-right">{score.r}</span>
          <span className="sc-mono text-xs tracking-widest uppercase text-[color:var(--sc-pink)]">{opLabel}</span>
        </div>
      </div>
      <div className="relative w-full max-w-[800px] aspect-[5/3] border border-white/10 rounded-sm overflow-hidden bg-black">
        <canvas ref={canvasRef} width={W} height={H} className="w-full h-full block" data-testid="pc-canvas" />
        {(paused || winner || error) && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm">
            <div className="text-center">
              {error ? (
                <>
                  <div className="sc-label" style={{ color: "var(--sc-pink)" }}>Disconnected</div>
                  <div className="sc-heading text-2xl font-bold mt-2">{error}</div>
                  <button onClick={cancelOnline} className="sc-btn mt-4 text-sm">Back to lobby</button>
                </>
              ) : winner ? (
                <>
                  <div className="sc-label mb-2" style={{ color: winner === "L" ? "var(--sc-cyan)" : "var(--sc-pink)" }}>Match Over</div>
                  <div className="sc-heading text-4xl font-black" data-testid="pc-winner">
                    {mode === "local"
                      ? (winner === "L" ? "Player 1 wins!" : "Player 2 wins!")
                      : (winner === "L"
                          ? (mode === "host" ? "You win!" : `@${peerName} wins!`)
                          : (mode === "host" ? `@${peerName} wins!` : "You win!"))}
                  </div>
                  {(mode === "local" || mode === "host") && (
                    <button onClick={restartMatch} className="sc-btn mt-4 text-sm" data-testid="pc-restart">Play again</button>
                  )}
                </>
              ) : (
                <>
                  <div className="sc-label text-zinc-300">Paused</div>
                  <div className="text-xs text-zinc-500 mt-2 sc-mono">Press SPACE to resume</div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
      <div className="text-[11px] text-zinc-500 sc-mono">
        SPACE to pause · {mode === "local" ? "both players on the same keyboard" : mode === "host" ? "You are the host · cyan (left)" : "You are the guest · pink (right)"}
      </div>
    </div>
  );
}
